import styled from '@emotion/styled';
import { withTheme } from '@emotion/react';
import React from 'react';
import { TestId } from '@components/test-ids';
import { Theme, ThemeProps } from '@theme/base';
import { useI18NextContext } from '@components/hooks';
import { TourAvailability, ItineraryType } from '@model/tours';
import { Grid } from '@mui/material';
import { Button, ButtonColor, ButtonSize, ButtonVariant } from '@components/material-ui';
import { Amount, DAY_MONTH_FORMAT_SHORT, ISO_8601_DATE_FORMAT, TripTypes } from '@model/common';
import moment from 'moment';
import { mq } from '@styles/breakpoints';
import { RTapNumber } from '@components/common/r-tap-number';
import { Icon } from '@components/common/generic-icon';
import { Icons } from '@model/common/icons';
import { DiscountPrice } from './TourDiscount';
import { isServer } from '@util/is-server';
import {
  getAlternativeItineraryLink,
  TourAlternativeItineraryPayload
} from '@model/tours/get-alternative-itinerary-link';

/* ***************** *
 *       Types       *
 * ***************** */
export interface TourPricingRowProps {
  theme?: Theme;
  availability: TourAvailability;
  onSubmit: (payload: TourAvailability) => void;
  testId?: string;
  isDisabled?: boolean;
  tripType?: TripTypes;
  totalGuests?: number;
}

/* ***************** *
 *       Styles      *
 * ***************** */
const Container = styled.div(({ theme }: ThemeProps) => ({
  borderTop: `1px solid ${theme.custom.colors.group10.light}`,
  backgroundColor: theme.custom.colors.group10.lighter
}));

const ArrowIcon = styled(Icon)(({ theme }: ThemeProps) => ({
  fontSize: '1.6rem',
  color: theme.custom.colors.white
}));

interface RowWrapperProps extends ThemeProps {
  isSoldOut: boolean;
  isPrice: boolean;
  isCTA: boolean;
}

const RowWrapper: any = styled.span(({ theme, isSoldOut, isPrice, isCTA }: RowWrapperProps) => ({
  ...(theme.custom.typography.paragraph as any),
  padding: theme.custom.spacing.large,
  display: 'flex',
  height: '100%',
  alignItems: 'center',
  justifyContent: 'stretch',
  ...(isSoldOut && {
    textDecoration: 'line-through'
  }),
  ...(isPrice && {
    fontWeight: 'bold'
  }),
  ...(isCTA && {
    paddingTop: 0
  }),

  [mq.medium]: {
    ...(theme.custom.typography.paragraph as any),
    padding: `0 ${theme.custom.spacing.large}px`,
    height: 80
  }
}));

const BookNow = styled.div({
  width: '100%'
});

const StyledButton: any = styled(Button)(({ theme }: ThemeProps) => {
  return {
    ['.MuiButtonBase-root']: {
      ':disabled': {
        backgroundColor: theme.custom.colors.group2.base,
        color: theme.custom.colors.white,
        opacity: 0.5
      }
    }
  };
});

const AlternativeItineraryStyledButton: any = styled(Button)(({ theme }: ThemeProps) => {
  return {
    ['.MuiButtonBase-root']: {
      ':disabled': {
        backgroundColor: theme.custom.colors.group2.base,
        color: theme.custom.colors.white,
        opacity: 0.5
      },
      minHeight: 50,
      lineHeight: 1.3
    }
  };
});

const NoPrice = styled.span({});

const InfoIcon = styled(Icon)(({ theme }: ThemeProps) => ({
  fontSize: theme.custom.spacing.medium,
  color: theme.custom.colors.group2.base
}));

/* ***************************** *
 *    TourPricingRowComponent    *
 * ***************************** */

export const formatDate = (date: string, format: string = DAY_MONTH_FORMAT_SHORT) =>
  moment(date).parseZone().format(format);

export const TourPricingRowComponent = (props: TourPricingRowProps) => {
  const t: any = useI18NextContext();
  const { availability, onSubmit, isDisabled, tripType, totalGuests } = props;
  const { callToBook, soldOut, price, departureDate, returnDate, alternativeItinerary, airports } = availability;
  const phoneNumber = t('common__brand_telephone');

  /* *** RENDERERS *** */
  const renderCTA = () => {
    if (callToBook) {
      return (
        <BookNow>
          {t('tour-product__label--call-to-book')}
          <br />
          <RTapNumber telephoneNumber={phoneNumber} />
        </BookNow>
      );
    }

    if (soldOut) {
      return <BookNow>{t('tour-product__label--sold-out')}</BookNow>;
    }

    if (alternativeItinerary && typeof alternativeItinerary !== 'boolean') {
      const handleAlternativeItineraryRedirect = () => {
        const payload: TourAlternativeItineraryPayload = {
          path: alternativeItinerary.path,
          airport: airports && airports.length > 0 ? airports[0].code : undefined,
          alternativeDate: formatDate(departureDate, ISO_8601_DATE_FORMAT),
          guests: totalGuests,
          tripType
        };

        const redirectUrl = getAlternativeItineraryLink(payload);

        if (!isServer) {
          window.open(redirectUrl, '_self');
        }
      };

      return (
        <BookNow>
          <AlternativeItineraryStyledButton
            variant={ButtonVariant.OUTLINED}
            label={t(
              alternativeItinerary.type === ItineraryType.REVERSE
                ? 'tour-product__label__cta--reverseItinerary'
                : 'tour-product__label__cta--alternativeItinerary'
            )}
            color={ButtonColor.SECONDARY}
            size={ButtonSize.SMALL}
            endIcon={<InfoIcon name={Icons.INFO} />}
            onClick={handleAlternativeItineraryRedirect}
            disabled={isDisabled}
          />
        </BookNow>
      );
    }

    return (
      <BookNow>
        <StyledButton
          variant={ButtonVariant.CONTAINED}
          label={t('common__label--continue')}
          color={ButtonColor.SECONDARY}
          size={ButtonSize.SMALL}
          endIcon={<ArrowIcon name={Icons.ARROW_RIGHT} />}
          onClick={() => {
            onSubmit(availability);
          }}
          disabled={isDisabled}
        />
      </BookNow>
    );
  };

  const formattedDepartureDate = formatDate(departureDate);
  const formattedReturnDate = formatDate(returnDate, DAY_MONTH_FORMAT_SHORT);
  return (
    <Container data-testid={props.testId || TestId.tourPricing.availabilityRow}>
      <Grid container spacing={0}>
        <Grid item xs={6} md={3}>
          <RowWrapper isSoldOut={soldOut}>
            {formattedDepartureDate} - {formattedReturnDate}
          </RowWrapper>
        </Grid>
        <Grid item xs={6} md={5}>
          <RowWrapper isSoldOut={soldOut} isPrice>
            {price.original && price.discount ? (
              <DiscountPrice price={price} />
            ) : price.amount ? (
              <>{new Amount(price.amount, price.currency.code).format()}</>
            ) : (
              <NoPrice>{t('common__label--price-on-request')}</NoPrice>
            )}
          </RowWrapper>
        </Grid>
        <Grid item xs={12} md={4}>
          <RowWrapper isSoldOut={soldOut} isCTA>
            {renderCTA()}
          </RowWrapper>
        </Grid>
      </Grid>
    </Container>
  );
};

export const TourPricingRow = withTheme(TourPricingRowComponent);
